"use client";

import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Separator,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import type {
  Origin,
  MeiliOrigin,
  MeiliOriginProcessedStatus,
} from "@/generated/fastapi";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import { getHumanReadableTimestamp } from "@/shared/utils";
import OriginHighlightedText from "./OriginHighlightedText";
import OriginExtendForm from "./OriginExtendForm";
import type { ExtendableOriginInput } from "../types";

type OriginItemDetailProps = {
  initialMode?: "preview" | "extend";
  origin: MeiliOrigin;
  onCancelExtend?: () => void;
  onExtendSuccess?: (origin: Origin) => void;
  searchQuery?: string;
};

function getProcessedStatus(origin: MeiliOrigin) {
  const wasProcessed =
    origin.processed_status === ("PROCESSED" as MeiliOriginProcessedStatus);

  return {
    label: wasProcessed ? "Source shaped" : "Source material",
    colorPalette: wasProcessed ? ("green" as const) : ("gray" as const),
  };
}

function getExtendableOriginInput(origin: MeiliOrigin): ExtendableOriginInput {
  return {
    id: origin.objectID,
    raw_text_input: origin.raw_text_input,
    language: origin.language,
    latest_at: origin.latest_at,
  };
}

export default function OriginItemDetail({
  initialMode = "preview",
  origin,
  onCancelExtend,
  onExtendSuccess,
  searchQuery,
}: OriginItemDetailProps) {
  const [mode, setMode] = useState(initialMode);
  const extendSectionRef = useRef<HTMLDivElement | null>(null);
  const shouldFocusExtendRef = useRef(initialMode === "extend");
  const status = getProcessedStatus(origin);
  const formattedTimestamp = getHumanReadableTimestamp(origin.latest_at);
  const languageLabel = origin.language?.toUpperCase() ?? "No language";
  const hasSearchQuery = !!searchQuery?.trim();
  const isExtending = mode === "extend";
  const handleCancelExtend = () => {
    setMode("preview");
    onCancelExtend?.();
  };
  const handleStartExtend = () => {
    shouldFocusExtendRef.current = true;
    setMode("extend");
  };

  useEffect(() => {
    if (!isExtending || !shouldFocusExtendRef.current) {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      extendSectionRef.current?.scrollIntoView({
        block: "start",
        behavior: "smooth",
      });
      extendSectionRef.current?.focus({ preventScroll: true });
      shouldFocusExtendRef.current = false;
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [isExtending]);

  return (
    <Stack gap="4" align="stretch">
      <GenericDrawerInfo
        description="Preview this source material as stored in Backlog."
        items={[
          {
            label: "Preview",
            colorPalette: "blue",
          },
          {
            label: status.label,
            colorPalette: status.colorPalette,
          },
          {
            label: languageLabel,
            colorPalette: origin.language ? "teal" : "gray",
          },
          {
            label: formattedTimestamp,
            colorPalette: "gray",
          },
          ...(hasSearchQuery
            ? [
                {
                  label: "Search highlighted",
                  colorPalette: "yellow" as const,
                },
              ]
            : []),
        ]}
      />

      <Separator />

      <Stack gap="2" align="stretch">
        <HStack justify="space-between" align="center" gap="3">
          <Heading size="sm">Content</Heading>
          <Badge variant="surface" colorPalette={status.colorPalette}>
            {status.label}
          </Badge>
        </HStack>
        <OriginHighlightedText
          searchQuery={searchQuery}
          text={origin.raw_text_input}
        />
      </Stack>

      <Separator />

      {isExtending ? (
        <Box
          ref={extendSectionRef}
          borderWidth="1"
          borderColor="moveon.surface.border"
          borderRadius="md"
          bg="moveon.surface.elevatedBg"
          p={{ base: "3", md: "4" }}
          tabIndex={-1}
        >
          <Stack gap="3" align="stretch">
            <Stack gap="1">
              <Heading size="sm">Extend source material</Heading>
              <Text fontSize="sm" color="moveon.text.muted" lineHeight="tall">
                Add more context to this stored source. The new text is appended
                to the existing source material.
              </Text>
            </Stack>
            <OriginExtendForm
              originToExtend={getExtendableOriginInput(origin)}
              onCancel={handleCancelExtend}
              onSuccess={(updatedOrigin) => {
                setMode("preview");
                onExtendSuccess?.(updatedOrigin);
              }}
              showOrientation={false}
            />
          </Stack>
        </Box>
      ) : (
        <HStack justify="flex-end">
          <Button
            type="button"
            bg="moveon.primary"
            color="white"
            onClick={handleStartExtend}
            aria-label="Extend source material"
            minW={{ base: "10", sm: "auto" }}
          >
            <GenericIcon icon="plus" />
            <Text as="span">Extend source material</Text>
          </Button>
        </HStack>
      )}
    </Stack>
  );
}
